// Supplier batch inline-edit and add-parts state for CallOffDetailModal.tsx,
// split out to keep CallOffDetailModal.tsx under the 500-line cap. No
// behaviour change from the original inline state — moved verbatim.
import { useState, useCallback } from 'react';
import { callOffAPI } from '../../../lib/api';
import type {
  CallOff, CallOffLine, AvailablePartForCallOff, CallOffDeliveryDestination,
  BatchInlineEditChange,
} from '../../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { warnAllocationShortfalls } from './allocationShortfallToast';

interface InlineEditChange {
  lineId: number;
  partNumber: string;
  oldQty: string;
  newQty: string;
}

interface UseCallOffInlineEditArgs {
  callOffId: number | null;
  callOff: CallOff | null;
  fetchDetail: () => Promise<void>;
  onUpdated: () => void;
  setActiveTab: (tab: string) => void;
}

export interface UseCallOffInlineEditResult {
  inlineEditMode: boolean;
  inlineEditChanges: Record<number, InlineEditChange>;
  savingBatchEdit: boolean;
  showAddParts: boolean;
  setShowAddParts: (value: boolean) => void;
  availableParts: AvailablePartForCallOff[];
  availableDeliveries: CallOffDeliveryDestination[];
  selectedDelivery: string;
  setSelectedDelivery: (value: string) => void;
  addPartsLoading: boolean;
  partQuantities: Record<number, number>;
  setPartQuantities: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  addingPart: boolean;
  availabilityByPart: Record<string, number>;
  /** Per part_number: raw availability + qty already on this call-off's
   *  warehouse lines, versus what edits would consume. */
  overByPart: Record<string, { effective: number; consumed: number; over: boolean }>;
  overLineCount: number;
  hasOverLimit: boolean;
  enterInlineEditMode: () => Promise<void>;
  cancelInlineEditMode: () => void;
  onQuantityChange: (line: CallOffLine, newQty: string) => void;
  removeLineItem: (line: CallOffLine) => void;
  saveBatchInlineEdit: () => Promise<void>;
  handleAddPart: (part: AvailablePartForCallOff) => Promise<void>;
  resetInlineEdit: () => void;
}

/**
 * Owns the supplier's batch inline quantity-edit mode and the add-parts
 * workflow it opens alongside — available parts/deliveries, per-part PO
 * availability, and the change set saved as one batch.
 *
 * @param args.callOffId - the call-off id being edited, or null
 * @param args.callOff - the loaded call-off, used to guard entry into edit mode
 * @param args.fetchDetail - refetch the call-off detail after a save
 * @param args.onUpdated - notify the caller (e.g. to refresh a list) after a save
 * @param args.setActiveTab - switches the modal to the Items tab on entering edit mode
 * @returns inline-edit/add-parts state plus the handlers CallOffDetailModal wires to the UI
 */
export function useCallOffInlineEdit({
  callOffId, callOff, fetchDetail, onUpdated, setActiveTab,
}: UseCallOffInlineEditArgs): UseCallOffInlineEditResult {
  // Supplier batch inline edit mode
  const [inlineEditMode, setInlineEditMode] = useState(false);
  const [inlineEditChanges, setInlineEditChanges] = useState<Record<number, InlineEditChange>>({});
  const [savingBatchEdit, setSavingBatchEdit] = useState(false);

  // Supplier add parts state
  const [showAddParts, setShowAddParts] = useState(false);
  const [availableParts, setAvailableParts] = useState<AvailablePartForCallOff[]>([]);
  const [availableDeliveries, setAvailableDeliveries] = useState<CallOffDeliveryDestination[]>([]);
  const [selectedDelivery, setSelectedDelivery] = useState<string>('');
  const [addPartsLoading, setAddPartsLoading] = useState(false);
  const [partQuantities, setPartQuantities] = useState<Record<number, number>>({});
  const [addingPart, setAddingPart] = useState(false);

  // Per-part PO availability fetched at edit-mode entry. Used to red-flag
  // warehouse lines whose qty exceeds what's free to call off.
  const [availabilityByPart, setAvailabilityByPart] = useState<Record<string, number>>({});

  const enterInlineEditMode = async () => {
    if (!callOffId || !callOff) return;
    setInlineEditMode(true);
    setInlineEditChanges({});
    setActiveTab('items');
    setAddPartsLoading(true);
    try {
      const [partsRes, deliveriesRes, availRes] = await Promise.all([
        callOffAPI.getAvailableParts(callOffId),
        callOffAPI.getAvailableDeliveries(callOffId),
        // Per-call-off endpoint scopes availability to the call-off's
        // customer + supplier, so suppliers get correct PO numbers (the
        // generic stock-planning endpoint scopes by the request user's
        // own company and returns 0 for suppliers).
        callOffAPI.getCallOffAvailability(callOffId),
      ]);
      setAvailableParts(partsRes.data || []);
      setAvailableDeliveries(deliveriesRes.data || []);
      const map: Record<string, number> = {};
      for (const [pn, qty] of Object.entries(availRes.data || {})) {
        map[pn] = parseFloat(qty) || 0;
      }
      setAvailabilityByPart(map);
    } catch {
      // Non-critical — degrade silently. Backend still validates on save.
      setAvailabilityByPart({});
    } finally {
      setAddPartsLoading(false);
    }
  };

  const cancelInlineEditMode = () => {
    setInlineEditMode(false);
    setInlineEditChanges({});
    setShowAddParts(false);
    fetchDetail();
  };

  const onQuantityChange = (line: CallOffLine, newQty: string) => {
    const parsed = parseFloat(newQty);
    if (isNaN(parsed) || parsed < 0) return;
    if (String(parsed) === line.requested_qty) {
      setInlineEditChanges(prev => {
        const updated = { ...prev };
        delete updated[line.id];
        return updated;
      });
    } else {
      setInlineEditChanges(prev => ({
        ...prev,
        [line.id]: { lineId: line.id, partNumber: line.part_number, oldQty: line.requested_qty, newQty: String(parsed) },
      }));
    }
  };

  const removeLineItem = (line: CallOffLine) => {
    if (!window.confirm(`Remove ${line.part_number} from this call-off?`)) return;
    onQuantityChange(line, '0');
  };

  const saveBatchInlineEdit = async () => {
    if (!callOffId) return;
    const changes = Object.values(inlineEditChanges);
    if (changes.length === 0) { toast.info('No changes to save'); return; }
    if (!window.confirm(`Save ${changes.length} change(s) to ${changes.length} part(s)?`)) return;
    setSavingBatchEdit(true);
    try {
      const batchChanges: BatchInlineEditChange[] = changes.map(c => ({ line_id: c.lineId, new_qty: parseFloat(c.newQty) }));
      const res = await callOffAPI.batchInlineEdit(callOffId, batchChanges);
      toast.success(`${changes.length} change(s) saved`);
      warnAllocationShortfalls(res.data.allocation_shortfalls);
      setInlineEditMode(false);
      setInlineEditChanges({});
      fetchDetail();
      onUpdated();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save changes'));
    } finally {
      setSavingBatchEdit(false);
    }
  };

  const handleAddPart = async (part: AvailablePartForCallOff) => {
    if (!callOffId) return;
    const qty = partQuantities[part.id] || 0;
    if (qty <= 0) { toast.error('Please enter a quantity greater than 0'); return; }
    if (!selectedDelivery) { toast.error('Please select a delivery destination'); return; }
    const delivery = availableDeliveries[parseInt(selectedDelivery)];
    if (!delivery) return;
    setAddingPart(true);
    try {
      const addRes = await callOffAPI.addLines(callOffId, [{
        part_id: part.id,
        part_number: part.part_number,
        description: part.description,
        requested_qty: qty,
        pallet_qty: parseFloat(part.pallet_qty) || 0,
        load_qty: parseFloat(part.load_qty) || 0,
        delivery_type: delivery.delivery_type,
        delivery_address: delivery.delivery_address,
        delivery_destination_id: delivery.delivery_destination_id || undefined,
        // CR-7144: a direct drop's name lives on the line, so a part added here
        // lands in the same delivery the wizard rebuilds — not a nameless one.
        delivery_destination_name: delivery.delivery_destination_id
          ? ''
          : delivery.delivery_destination_name,
      }]);
      toast.success(`${part.part_number} added`);
      // CR-7389: adding to a confirmed call-off allocates delivery lines there
      // and then. Same warning as confirm — if the PO no longer covers the
      // quantity, the supplier will ship short unless someone is told.
      warnAllocationShortfalls(addRes.data.allocation_shortfalls);
      setPartQuantities(prev => ({ ...prev, [part.id]: 0 }));
      fetchDetail();
      onUpdated();
      const partsRes = await callOffAPI.getAvailableParts(callOffId);
      setAvailableParts(partsRes.data || []);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to add part');
    } finally {
      setAddingPart(false);
    }
  };

  const resetInlineEdit = useCallback(() => {
    setInlineEditMode(false);
    setInlineEditChanges({});
    setShowAddParts(false);
  }, []);

  // Aggregate per part_number: warehouse lines sharing a part share a PO
  // budget. effective = raw available + sum(originalQty for those lines).
  // consumed = sum(currentQty after edits). Over if consumed > effective.
  const overByPart: Record<string, { effective: number; consumed: number; over: boolean }> = {};
  if (callOff && inlineEditMode) {
    const groups: Record<string, CallOffLine[]> = {};
    for (const line of callOff.lines) {
      if (line.delivery_type !== 'warehouse') continue;
      (groups[line.part_number] = groups[line.part_number] || []).push(line);
    }
    for (const [pn, lines] of Object.entries(groups)) {
      const raw = availabilityByPart[pn] || 0;
      const own = lines.reduce((s, l) => s + (parseFloat(l.requested_qty) || 0), 0);
      const consumed = lines.reduce((s, l) => {
        const ch = inlineEditChanges[l.id];
        const q = ch ? parseFloat(ch.newQty) : parseFloat(l.requested_qty);
        return s + (q || 0);
      }, 0);
      const effective = raw + own;
      overByPart[pn] = { effective, consumed, over: consumed > effective };
    }
  }
  const overLineCount = callOff ? callOff.lines.filter(
    l => l.delivery_type === 'warehouse' && overByPart[l.part_number]?.over
  ).length : 0;
  const hasOverLimit = overLineCount > 0;

  return {
    inlineEditMode, inlineEditChanges, savingBatchEdit,
    showAddParts, setShowAddParts, availableParts, availableDeliveries,
    selectedDelivery, setSelectedDelivery, addPartsLoading, partQuantities,
    setPartQuantities, addingPart, availabilityByPart,
    overByPart, overLineCount, hasOverLimit,
    enterInlineEditMode, cancelInlineEditMode, onQuantityChange, removeLineItem,
    saveBatchInlineEdit, handleAddPart, resetInlineEdit,
  };
}
